import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Modal, Button, Form, Badge, Row, Col, Card } from 'react-bootstrap';
import { FaArrowRight, FaTrash, FaGripVertical, FaSearch, FaTimes, FaListUl } from 'react-icons/fa';

/** Rows effective for the role: custom subset/order or all active in Object Manager order. */
function getEffectiveAssignmentRows(values, useCustom, orderedKeys) {
  const byKey = new Map(values.map((v) => [String(v.value_key), v]));
  if (useCustom && Array.isArray(orderedKeys) && orderedKeys.length > 0) {
    return orderedKeys.map((k) => byKey.get(String(k))).filter(Boolean);
  }
  return [...values].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
}

/**
 * Edit role assignment for one picklist / multipicklist field: limit values and reorder.
 * The right-hand "assigned" list is loaded from `role_object_permission.config.picklists[field]` (via
 * `initialState` from the parent) and saved back into that config on Save.
 */
const EditRolePermissionModal = ({
  show,
  onHide,
  fieldLabel,
  fieldApiName,
  /** @type {Array<{ value_key: string, value_label: string, is_default?: boolean, sort_order?: number }>} */
  values = [],
  /** @type {{ useCustom: boolean, orderedKeys: string[] }} */
  initialState,
  readOnly = false,
  /** @param {{ useCustom: boolean, orderedKeys: string[] }} next */
  onSave,
}) => {
  const [useCustom, setUseCustom] = useState(false);
  const [orderedKeys, setOrderedKeys] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  /** HTML5 DnD: React state from dragStart is not available yet on drop in the same gesture — use a ref. */
  const draggedIndexRef = useRef(null);

  useEffect(() => {
    if (!show || !initialState) return;
    setUseCustom(initialState.useCustom === true);
    setOrderedKeys(Array.isArray(initialState.orderedKeys) ? [...initialState.orderedKeys] : []);
    setSearchTerm('');
    draggedIndexRef.current = null;
    setDraggedItem(null);
    setDragOverItem(null);
  }, [show, fieldApiName, initialState]);

  useEffect(() => {
    if (!show) {
      draggedIndexRef.current = null;
      setDraggedItem(null);
      setDragOverItem(null);
    }
  }, [show]);

  const allKeys = values.map((v) => v.value_key).filter(Boolean);

  const byKey = useMemo(() => new Map(values.map((v) => [String(v.value_key), v])), [values]);

  const availableRows = useMemo(() => {
    const sel = new Set(orderedKeys.map(String));
    return [...values]
      .filter((v) => v.value_key && !sel.has(String(v.value_key)))
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }, [values, orderedKeys]);

  const filteredAvailable = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return availableRows;
    return availableRows.filter(
      (v) =>
        String(v.value_label || '').toLowerCase().includes(q) ||
        String(v.value_key || '').toLowerCase().includes(q)
    );
  }, [availableRows, searchTerm]);

  const setUseCustomAndKeys = (nextUseCustom, baseKeys) => {
    setUseCustom(nextUseCustom);
    if (!nextUseCustom) {
      setOrderedKeys([...allKeys]);
    } else {
      setOrderedKeys(baseKeys.length ? [...baseKeys] : [...allKeys]);
    }
  };

  const moveToSelected = (v) => {
    if (readOnly) return;
    const k = String(v.value_key);
    if (!k || orderedKeys.includes(k)) return;
    if (!useCustom) setUseCustom(true);
    setOrderedKeys((prev) => [...prev, k]);
  };

  const moveToAvailable = (v) => {
    if (readOnly) return;
    const k = String(v.value_key);
    if (!useCustom) setUseCustom(true);
    setOrderedKeys((prev) => prev.filter((x) => String(x) !== k));
  };

  const handleDragStart = (e, index) => {
    if (readOnly) return;
    draggedIndexRef.current = index;
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const from = draggedIndexRef.current;
    if (from !== null && from !== index) {
      setDragOverItem(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    const from = draggedIndexRef.current;
    if (readOnly || from === null || from === index) {
      draggedIndexRef.current = null;
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }
    const next = [...orderedKeys];
    const [removed] = next.splice(from, 1);
    next.splice(index, 0, removed);
    setOrderedKeys(next);
    if (!useCustom) setUseCustom(true);
    draggedIndexRef.current = null;
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragEnd = () => {
    draggedIndexRef.current = null;
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleAssignedListDragOver = (e) => {
    if (readOnly || draggedIndexRef.current === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleSave = () => {
    if (readOnly || !onSave) return;
    onSave({
      useCustom,
      orderedKeys: useCustom ? [...orderedKeys] : [...allKeys],
    });
    onHide();
  };

  const handleClose = () => {
    onHide();
  };

  const picklistDisplayName = fieldLabel || fieldApiName || 'Picklist';

  const effectiveRows = useMemo(
    () => getEffectiveAssignmentRows(values, useCustom, orderedKeys),
    [values, useCustom, orderedKeys]
  );

  /** Left-aligned label + API name (+ optional list icon). */
  const renderPicklistValueBlock = (v, { listIcon = false } = {}) => (
    <div className="d-flex align-items-start gap-2 min-w-0 flex-grow-1">
      {listIcon ? (
        <FaListUl className="text-muted flex-shrink-0 mt-1" size={14} aria-hidden />
      ) : null}
      <div className="min-w-0 text-start flex-grow-1">
        <div className="fw-semibold text-truncate">{v.value_label || v.value_key}</div>
        <div className="small text-muted text-truncate">{v.value_key}</div>
        {v.is_default ? (
          <Badge bg="secondary" className="mt-1">
            Default
          </Badge>
        ) : null}
      </div>
    </div>
  );

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      scrollable
      enforceFocus={false}
      restoreFocus={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className="d-flex flex-wrap align-items-baseline gap-2">
          <span>Assign Picklist: {picklistDisplayName}</span>
          {readOnly ? (
            <span className="text-muted fw-normal fs-6">View only</span>
          ) : null}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {readOnly ? (
          <>
            <div className="mb-3">
              {useCustom ? (
                <Badge bg="info" text="dark">
                  Custom assignment
                </Badge>
              ) : (
                <Badge bg="light" text="dark" className="border">
                  All active values
                </Badge>
              )}
            </div>
            {!useCustom && values.length > 0 && (
              <p className="text-muted small mb-3">
                This role uses every active value. Order matches Object Manager unless a custom assignment is
                configured.
              </p>
            )}
            {useCustom && effectiveRows.length === 0 && values.length > 0 && (
              <p className="text-warning small mb-3">No values selected for this role.</p>
            )}
            {values.length === 0 ? (
              <p className="text-warning small mb-0">No active picklist values in Object Manager.</p>
            ) : (
              <Card className="border-0 shadow-sm mb-0">
                <Card.Header className="py-2 bg-light border-bottom">
                  <h6 className="mb-0 d-flex align-items-center">
                    <FaListUl className="me-2" />
                    Picklist values
                  </h6>
                  <small className="text-muted">
                    Assigned column shows values from this role&apos;s object permission{' '}
                    <code className="small">config.picklists</code> (when set); otherwise all active values.
                  </small>
                </Card.Header>
                <Card.Body className="pt-3">
                  <Row>
                    <Col md={5}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">
                          <Badge bg="secondary" className="me-2">
                            {filteredAvailable.length}
                          </Badge>
                          Available values
                        </h6>
                      </div>
                      <div className="mb-2 position-relative">
                        <FaSearch
                          className="position-absolute text-muted"
                          style={{ left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13 }}
                          aria-hidden
                        />
                        <Form.Control
                          type="text"
                          size="sm"
                          placeholder="Search…"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          style={{ paddingLeft: 32, paddingRight: searchTerm ? 32 : undefined }}
                          aria-label="Search available picklist values"
                        />
                        {searchTerm ? (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 position-absolute text-muted"
                            style={{ right: 6, top: '50%', transform: 'translateY(-50%)' }}
                            onClick={() => setSearchTerm('')}
                            title="Clear search"
                            aria-label="Clear search"
                          >
                            <FaTimes size={12} />
                          </button>
                        ) : null}
                      </div>
                      <div
                        className="border rounded p-2"
                        style={{ maxHeight: 360, overflowY: 'auto', backgroundColor: '#f8f9fa' }}
                      >
                        {filteredAvailable.length === 0 ? (
                          <div className="text-center text-muted py-4 small">
                            {searchTerm.trim()
                              ? `No values match “${searchTerm}”.`
                              : 'No values here (all active values are assigned).'}
                          </div>
                        ) : (
                          filteredAvailable.map((v) => (
                            <div
                              key={v.value_key}
                              className="d-flex align-items-center gap-2 p-2 mb-2 bg-white rounded border"
                            >
                              {renderPicklistValueBlock(v, { listIcon: true })}
                            </div>
                          ))
                        )}
                      </div>
                    </Col>
                    <Col md={7}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">
                          <Badge bg="primary" className="me-2">
                            {orderedKeys.length}
                          </Badge>
                          Assigned (permission <code className="small">config</code>)
                        </h6>
                        <small className="text-muted">Read only</small>
                      </div>
                      <div
                        className="border rounded p-2"
                        style={{ maxHeight: 360, overflowY: 'auto', backgroundColor: '#f8f9fa' }}
                      >
                        {orderedKeys.length === 0 ? (
                          <div className="text-center text-muted py-4 small">No assigned values.</div>
                        ) : (
                          orderedKeys.map((key, index) => {
                            const v = byKey.get(String(key));
                            if (!v) {
                              return (
                                <div
                                  key={String(key)}
                                  className="p-2 mb-2 bg-white rounded border border-warning small text-muted"
                                >
                                  Inactive or missing value: <code>{key}</code>
                                </div>
                              );
                            }
                            return (
                              <div
                                key={key}
                                className="d-flex align-items-start gap-2 p-2 mb-2 bg-white rounded border"
                              >
                                <span
                                  className="text-muted small flex-shrink-0 mt-1 tabular-nums"
                                  style={{ width: '1.5rem', textAlign: 'right' }}
                                >
                                  {index + 1}.
                                </span>
                                {renderPicklistValueBlock(v, { listIcon: false })}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </>
        ) : (
          <>
            <Form.Check
              type="switch"
              id={`edit-role-picklist-${fieldApiName}`}
              className="mb-3"
              label="Limit this role to specific values (custom assignment)"
              checked={useCustom}
              disabled={values.length === 0}
              onChange={(e) => setUseCustomAndKeys(e.target.checked, orderedKeys)}
            />
            {!useCustom && values.length > 0 && (
              <p className="text-muted small mb-3">
                All active values appear on the right. Drag to reorder (saves a custom display order). Turn on
                the switch above to move values to the left and limit this role to a subset.
              </p>
            )}
            {values.length === 0 && (
              <p className="text-warning small mb-0">No active values for this field.</p>
            )}
            {values.length > 0 && (
              <Card className="border-0 shadow-sm mb-0">
                <Card.Header className="py-2 bg-light border-bottom">
                  <h6 className="mb-0 d-flex align-items-center">
                    <FaListUl className="me-2" />
                    Picklist values
                  </h6>
                  <small className="text-muted">
                    Right column is what gets saved to <code className="small">role_object_permission.config.picklists</code>{' '}
                    for this field. Drag to reorder; add/remove to change the set. Left: active values not in
                    that config.
                  </small>
                </Card.Header>
                <Card.Body className="pt-3">
                  <Row>
                    <Col md={5}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">
                          <Badge bg="secondary" className="me-2">
                            {filteredAvailable.length}
                          </Badge>
                          Available values
                        </h6>
                      </div>
                      <div className="mb-2 position-relative">
                        <FaSearch
                          className="position-absolute text-muted"
                          style={{ left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13 }}
                          aria-hidden
                        />
                        <Form.Control
                          type="text"
                          size="sm"
                          placeholder="Search label or API name…"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          style={{ paddingLeft: 32, paddingRight: searchTerm ? 32 : undefined }}
                          aria-label="Search available picklist values"
                        />
                        {searchTerm ? (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 position-absolute text-muted"
                            style={{ right: 6, top: '50%', transform: 'translateY(-50%)' }}
                            onClick={() => setSearchTerm('')}
                            title="Clear search"
                            aria-label="Clear search"
                          >
                            <FaTimes size={12} />
                          </button>
                        ) : null}
                      </div>
                      {searchTerm.trim() ? (
                        <div className="small text-muted mb-2">
                          Showing {filteredAvailable.length} of {availableRows.length} available
                        </div>
                      ) : null}
                      <div
                        className="border rounded p-2"
                        style={{ maxHeight: 360, overflowY: 'auto', backgroundColor: '#f8f9fa' }}
                      >
                        {filteredAvailable.length === 0 ? (
                          <div className="text-center text-muted py-4 small">
                            {searchTerm.trim()
                              ? `No values match “${searchTerm}”.`
                              : availableRows.length === 0
                                ? 'All active values are in the selected list.'
                                : 'No available values.'}
                          </div>
                        ) : (
                          filteredAvailable.map((v) => (
                            <div
                              key={v.value_key}
                              className="d-flex align-items-center gap-2 p-2 mb-2 bg-white rounded border"
                              style={{ cursor: 'pointer' }}
                              role="button"
                              tabIndex={0}
                              onClick={() => moveToSelected(v)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  moveToSelected(v);
                                }
                              }}
                            >
                              {renderPicklistValueBlock(v, { listIcon: true })}
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="flex-shrink-0 ms-auto"
                                draggable={false}
                                title="Add to selected"
                                aria-label={`Add ${v.value_label || v.value_key} to selected`}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveToSelected(v);
                                }}
                              >
                                <FaArrowRight />
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    </Col>
                    <Col md={7}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">
                          <Badge bg="primary" className="me-2">
                            {orderedKeys.length}
                          </Badge>
                          Assigned (saved to permission <code className="small">config</code>)
                        </h6>
                        <small className="text-muted">Drag to reorder · Remove to return to available</small>
                      </div>
                      <div
                        className="border rounded p-2"
                        style={{ maxHeight: 360, overflowY: 'auto', backgroundColor: '#f8f9fa' }}
                        onDragOver={handleAssignedListDragOver}
                      >
                        {orderedKeys.length === 0 ? (
                          <div className="text-center text-muted py-4 small">
                            <p className="mb-1">No values selected for this role.</p>
                            <span>Add values from the left panel.</span>
                          </div>
                        ) : (
                          orderedKeys.map((key, index) => {
                            const v = byKey.get(String(key));
                            if (!v) {
                              return (
                                <div
                                  key={String(key)}
                                  className="d-flex justify-content-between align-items-center p-2 mb-2 bg-white rounded border border-warning"
                                >
                                  <span className="small text-muted">
                                    Inactive or missing value: <code>{key}</code>
                                  </span>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    draggable={false}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={() => {
                                      if (!useCustom) setUseCustom(true);
                                      setOrderedKeys((prev) =>
                                        prev.filter((x) => String(x) !== String(key))
                                      );
                                    }}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              );
                            }
                            return (
                              <div
                                key={key}
                                draggable={!readOnly}
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={(e) => handleDragOver(e, index)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, index)}
                                onDragEnd={handleDragEnd}
                                className={`d-flex align-items-start gap-2 p-2 mb-2 bg-white rounded border ${
                                  draggedItem === index ? 'opacity-50 shadow' : ''
                                } ${dragOverItem === index ? 'border-primary border-2' : ''}`}
                                style={{ cursor: readOnly ? 'default' : 'grab' }}
                              >
                                <FaGripVertical
                                  className="text-muted flex-shrink-0 mt-1"
                                  style={{ cursor: readOnly ? 'default' : 'grab' }}
                                  aria-hidden
                                />
                                <span
                                  className="text-muted small flex-shrink-0 mt-1 tabular-nums"
                                  style={{ width: '1.5rem', textAlign: 'right' }}
                                >
                                  {index + 1}.
                                </span>
                                {renderPicklistValueBlock(v, { listIcon: false })}
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  className="flex-shrink-0 align-self-center ms-auto"
                                  draggable={false}
                                  title="Remove from role"
                                  aria-label={`Remove ${v.value_label || v.value_key}`}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onClick={() => moveToAvailable(v)}
                                >
                                  <FaTrash />
                                </Button>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
        {!readOnly && onSave && (
          <Button variant="primary" onClick={handleSave} disabled={values.length === 0 && useCustom}>
            Save
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default EditRolePermissionModal;
